import { useId, useState } from 'react'
import { Modal } from './modal.jsx'
import type { JSX } from 'react'

/**
 * The one question worth interrupting someone for.
 *
 * ## Why only destroy
 *
 * Every other control in this console either reads, or creates and updates
 * infrastructure that can be created again. A `plan` and an `output` change
 * nothing; an `apply` that goes wrong can be applied again — and *is* how the
 * product works, which is exactly why it must **not** get a confirmation.
 * Gating the ordinary path trains people to click through the dialog, and a
 * dialog people click through protects nothing.
 *
 * A successful `destroy` is not recoverable from this side. The Terraform state
 * lives in the customer's own S3 bucket and the diagnostics role is explicitly
 * denied `s3:GetObject` on it.
 *
 * ## Why it says what a failure leaves behind
 *
 * Because that is the outcome people do not expect, and it is already encoded in
 * `shared/src/deployments/deployment.ts`: a failed destroy is *not* `destroyed`.
 * It leaves the instance up and possibly half torn down, stranding real
 * infrastructure nobody is watching. Nothing told anyone that before IM-79.
 *
 * ## Why it is not a control
 *
 * `authorize` checks `deployment:destroy` on `POST …/runs/destroy` regardless,
 * and records the decision either way. This is friction against a mis-click and
 * nothing more — anyone who can reach the endpoint can skip it. It must never be
 * described, here or anywhere, as though it protected something.
 *
 * ## Why a native `<dialog>`
 *
 * `showModal` brings focus trapping, Esc, an inert background and the top layer,
 * none of which is worth reimplementing. `window.confirm` could carry none of
 * the above and blocks the event loop besides. jsdom 29 has `HTMLDialogElement`
 * but implements neither `showModal` nor `close`, so `web/src/test-setup.ts`
 * stands in for exactly those two — see the note there.
 */
export const ConfirmDestroy = ({
  tenantId,
  accountKey,
  deploymentId,
  onConfirm,
  onCancel,
}: {
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  readonly onConfirm: () => void
  readonly onCancel: () => void
}): JSX.Element => {
  const [typed, setTyped] = useState('')
  const inputId = useId()

  /**
   * Typing the id exactly is what enables the button.
   *
   * On a deployment's own screen the id is fixed and read-only, so re-typing it
   * is real friction. On the customer screen the caller typed it a moment ago —
   * still a second deliberate act, and still the only place the customer, the
   * account and the deployment are named together.
   */
  const matches = typed === deploymentId

  return (
    <Modal title="Destroy this instance?" onCancel={onCancel}>
      {/* The three facts the caller cannot see from the button: it sits inside a
        `<details>` on two different screens, and the account key is nowhere else
        on the page at the point of clicking. */}
      <dl className="detail">
        <dt>Customer</dt>
        <dd>
          <code>{tenantId}</code>
        </dd>
        <dt>Cloud account</dt>
        <dd>
          <code>{accountKey}</code>
        </dd>
        <dt>Deployment</dt>
        <dd>
          <code>{deploymentId}</code>
        </dd>
      </dl>

      <p role="alert">
        This tears down the customer&rsquo;s instance. It cannot be undone from here — the Terraform
        state is in their account, not ours.
      </p>

      {/* The outcome people do not expect. See the note above. */}
      <p className="muted">
        If the destroy fails part-way it does not count as destroyed: the instance stays up,
        possibly half torn down, and someone has to go and look.
      </p>

      <div className="field">
        <label htmlFor={inputId}>
          Type <code>{deploymentId}</code> to confirm
        </label>
        <input
          id={inputId}
          value={typed}
          autoComplete="off"
          // Focus lands here rather than on a button, so the first thing the
          // dialog asks for is the deliberate act rather than a click.
          autoFocus
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>

      <div className="actions">
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
        {/* Names its target rather than repeating the word on the button that
          opened this. Two controls reading "Destroy" on one screen is ambiguous
          for a screen reader and for anyone glancing at it — and restating what
          is about to be destroyed is worth doing at the moment of commitment. */}
        <button type="button" className="danger" disabled={!matches} onClick={onConfirm}>
          Destroy {deploymentId}
        </button>
      </div>
    </Modal>
  )
}
